import { useCallback, useEffect, useRef, useState } from 'react';

import type { SubScreen, SubScreenActions } from './index';

type SubscreenEntry = {
	subscreen: SubScreen;
	/** Monotonically increasing key used to force React to remount the overlay on each push,
	 *  which ensures the FadeIn entrance animation fires every time a new subscreen appears. */
	key: number;
};

type UseSubscreenStackResult = {
	activeSubscreen: SubScreen | null;
	/** Stable key for the active subscreen — changes on every push so React remounts the overlay. */
	activeSubscreenKey: number;
	subscreenActions: SubScreenActions;
};

/**
 * Manages the subscreen stack state for the side nav modal.
 * Returns the active (top-most) subscreen and actions to push/pop subscreens.
 * Clears the stack whenever `isOpen` transitions to false.
 */
export const useSubscreenStack = (isOpen: boolean): UseSubscreenStackResult => {
	const [subscreenStack, setSubscreenStack] = useState<SubscreenEntry[]>([]);
	const keyCounter = useRef(0);

	const pushSubscreen = useCallback((subscreen: SubScreen) => {
		const key = ++keyCounter.current;
		setSubscreenStack((prev) => [...prev, { subscreen, key }]);
	}, []);

	const popSubscreen = useCallback(() => {
		setSubscreenStack((prev) => prev.slice(0, -1));
	}, []);

	// Clear subscreen stack when modal closes
	useEffect(() => {
		if (!isOpen) {
			setSubscreenStack([]);
		}
	}, [isOpen]);

	const activeEntry = subscreenStack.length > 0 ? subscreenStack[subscreenStack.length - 1] : null;

	const subscreenActions: SubScreenActions = {
		pushSubscreen,
		popSubscreen,
		hasSubscreen: subscreenStack.length > 0,
	};

	return {
		activeSubscreen: activeEntry?.subscreen ?? null,
		activeSubscreenKey: activeEntry?.key ?? 0,
		subscreenActions,
	};
};
